// SPDX-License-Identifier: AGPL-3.0-or-later

import i18n from '@app/app/I18n';
import Accessibility from '@app/features/accessibility/state/Accessibility';
import Config from '@app/features/app/config/Config';
import {isMobileOrTabletUserAgent} from '@app/features/platform/notifications/NotificationAlertOptions';
import {type ClientInfo, getClientInfo, getClientInfoSync} from '@app/features/platform/utils/ClientInfo';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import {isDesktop} from '@app/features/ui/utils/NativeUtils';
import {isStandalonePwa} from '@app/features/ui/utils/PwaUtils';
import {formatReleaseChannelLabel} from '@fluxer/desktop_ipc/src/DesktopLabels';

export interface BugReportContext {
	clientShell: 'Web' | 'PWA' | 'Desktop';
	browserLabel: string;
	osLabel: string;
	deviceClass: 'Desktop' | 'Phone' | 'Tablet';
	layoutMode: 'Mobile layout' | 'Desktop layout';
	viewport: string;
	buildLabel: string;
	locale: string;
	/** Tier 3 diagnostics (always includes app RM setting; others when relevant). */
	diagnosticTags: ReadonlyArray<string>;
}

export const BUG_REPORT_MESSAGE_SEPARATOR = '\n\n';

type NetworkInformationLike = {
	effectiveType?: string;
};

function normalizeBrowserName(name: string | undefined): string {
	if (!name) {
		return 'Unknown';
	}
	if (name === 'Microsoft Edge') {
		return 'Edge';
	}
	return name;
}

function majorBrowserVersion(version: string | undefined): string {
	if (!version) {
		return '';
	}
	const major = version.split('.')[0];
	return major ?? '';
}

export function formatBrowserLabel(info: ClientInfo): string {
	if (info.desktopElectronVersion && info.desktopChromeVersion) {
		const version = majorBrowserVersion(info.desktopChromeVersion);
		return version ? `Chrome ${version}` : 'Chrome';
	}
	const name = normalizeBrowserName(info.browserName);
	const version = majorBrowserVersion(info.browserVersion);
	return version ? `${name} ${version}` : name;
}

export function formatOsLabel(info: ClientInfo): string {
	const osName = info.osName ?? 'Unknown';
	const osVersion = info.osVersion?.trim();
	if (!osVersion) {
		return osName;
	}
	const arch = info.desktopArch ?? info.arch;
	return arch ? `${osName} ${osVersion} · ${arch}` : `${osName} ${osVersion}`;
}

export function formatBuildLabel(info: ClientInfo): string {
	const releaseChannel = formatReleaseChannelLabel(Config.PUBLIC_RELEASE_CHANNEL);
	const buildVersion = Config.PUBLIC_BUILD_VERSION || 'dev';
	const desktopChannel = info.desktopChannel ? formatReleaseChannelLabel(info.desktopChannel) : null;
	const primaryDesktopChannel = desktopChannel ?? releaseChannel;
	const webChannelPrefix = info.desktopVersion && primaryDesktopChannel === releaseChannel ? '' : `${releaseChannel} `;
	const parts: Array<string> = [];
	if (info.desktopVersion) {
		parts.push(`${primaryDesktopChannel} Desktop ${info.desktopVersion}`);
	}
	parts.push(`${webChannelPrefix}Web ${buildVersion}`.trim());
	return parts.join(' · ');
}

function getClientShell(): BugReportContext['clientShell'] {
	if (isDesktop()) {
		return 'Desktop';
	}
	if (isStandalonePwa()) {
		return 'PWA';
	}
	return 'Web';
}

function getDeviceClass(userAgent: string, maxTouchPoints: number): BugReportContext['deviceClass'] {
	if (isDesktop()) {
		return 'Desktop';
	}
	const isIpad = /iPad/i.test(userAgent) || (/Macintosh/i.test(userAgent) && maxTouchPoints > 1);
	if (isIpad) {
		return 'Tablet';
	}
	if (isMobileOrTabletUserAgent(userAgent, maxTouchPoints)) {
		return 'Phone';
	}
	return 'Desktop';
}

function getViewportLabel(): string {
	if (typeof window === 'undefined') {
		return '0×0';
	}
	return `${window.innerWidth}×${window.innerHeight}`;
}

/**
 * Always includes the app reduced-motion setting. Other Tier 3 tags are omitted unless
 * they are non-default or resolve ambiguity (e.g. iPad vs Mac).
 */
export function formatReducedMotionDiagnosticTag(options: {
	syncReducedMotionWithSystem: boolean;
	useReducedMotion: boolean;
}): string {
	const effective = options.useReducedMotion ? 'on' : 'off';
	return options.syncReducedMotionWithSystem ? `RM: system ${effective}` : `RM: ${effective}`;
}

export function collectDiagnosticTags(options: {
	userAgent: string;
	maxTouchPoints: number;
	deviceClass: BugReportContext['deviceClass'];
	onLine: boolean;
	syncReducedMotionWithSystem: boolean;
	useReducedMotion: boolean;
	colorScheme: 'dark' | 'light' | null;
	effectiveNetworkType: string | null;
}): Array<string> {
	const tags: Array<string> = [
		formatReducedMotionDiagnosticTag({
			syncReducedMotionWithSystem: options.syncReducedMotionWithSystem,
			useReducedMotion: options.useReducedMotion,
		}),
	];
	if (!options.onLine) {
		tags.push('Offline');
	}
	if (options.effectiveNetworkType === 'slow-2g' || options.effectiveNetworkType === '2g') {
		tags.push(`Network: ${options.effectiveNetworkType}`);
	}
	const touchPoints = options.maxTouchPoints;
	const looksLikeMac = /Macintosh/i.test(options.userAgent);
	if (touchPoints > 1 && looksLikeMac && options.deviceClass === 'Tablet') {
		tags.push(`Touch: ${touchPoints}`);
	} else if (touchPoints > 0 && options.deviceClass === 'Desktop' && !isDesktop()) {
		tags.push(`Touch: ${touchPoints}`);
	}
	if (options.colorScheme === 'dark') {
		tags.push('Dark');
	}
	return tags;
}

function readColorScheme(): 'dark' | 'light' | null {
	if (typeof window === 'undefined' || !window.matchMedia) {
		return null;
	}
	if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
		return 'dark';
	}
	if (window.matchMedia('(prefers-color-scheme: light)').matches) {
		return 'light';
	}
	return null;
}

function readEffectiveNetworkType(): string | null {
	if (typeof navigator === 'undefined') {
		return null;
	}
	const connection = (navigator as Navigator & {connection?: NetworkInformationLike}).connection;
	return connection?.effectiveType ?? null;
}

export function buildBugReportContext(info: ClientInfo, overrides: Partial<BugReportContext> = {}): BugReportContext {
	const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
	const maxTouchPoints = typeof navigator !== 'undefined' ? (navigator.maxTouchPoints ?? 0) : 0;
	const deviceClass = overrides.deviceClass ?? getDeviceClass(userAgent, maxTouchPoints);
	const onLine = typeof navigator !== 'undefined' ? navigator.onLine : true;
	const colorScheme = readColorScheme();
	return {
		clientShell: overrides.clientShell ?? getClientShell(),
		browserLabel: overrides.browserLabel ?? formatBrowserLabel(info),
		osLabel: overrides.osLabel ?? formatOsLabel(info),
		deviceClass,
		layoutMode: overrides.layoutMode ?? (MobileLayout.enabled ? 'Mobile layout' : 'Desktop layout'),
		viewport: overrides.viewport ?? getViewportLabel(),
		buildLabel: overrides.buildLabel ?? formatBuildLabel(info),
		locale: overrides.locale ?? i18n.locale,
		diagnosticTags:
			overrides.diagnosticTags ??
			collectDiagnosticTags({
				userAgent,
				maxTouchPoints,
				deviceClass,
				onLine,
				syncReducedMotionWithSystem: Accessibility.syncReducedMotionWithSystem,
				useReducedMotion: Accessibility.useReducedMotion,
				colorScheme,
				effectiveNetworkType: readEffectiveNetworkType(),
			}),
	};
}

export function formatBugReportPrefix(context: BugReportContext): string {
	const parts = [
		'[BUG]',
		context.clientShell,
		context.browserLabel,
		context.osLabel,
		context.deviceClass,
		context.layoutMode,
		context.viewport,
		context.buildLabel,
		context.locale,
		...context.diagnosticTags,
	];
	return parts.join(' · ');
}

export function formatBugReportMessage(userMessage: string, context: BugReportContext): string {
	const trimmed = userMessage.trim();
	if (!trimmed) {
		return formatBugReportPrefix(context);
	}
	return `${formatBugReportPrefix(context)}${BUG_REPORT_MESSAGE_SEPARATOR}${trimmed}`;
}

/** Sync length budget for the metadata block plus the separator before the user message. */
export function getBugReportMetadataOverheadLength(info: ClientInfo = getClientInfoSync()): number {
	return formatBugReportPrefix(buildBugReportContext(info)).length + BUG_REPORT_MESSAGE_SEPARATOR.length;
}

export function getBugReportMessageLength(userMessage: string, info: ClientInfo = getClientInfoSync()): number {
	const trimmed = userMessage.trim();
	if (trimmed.length === 0) {
		return getBugReportMetadataOverheadLength(info) - BUG_REPORT_MESSAGE_SEPARATOR.length;
	}
	return getBugReportMetadataOverheadLength(info) + trimmed.length;
}

export async function formatBugReportMessageWithClientInfo(userMessage: string): Promise<string> {
	const info = await getClientInfo();
	return formatBugReportMessage(userMessage, buildBugReportContext(info));
}
